import React from "react";
import Modal from "../../../../../components/ui/Modal";
import Button from "../../../../../components/ui/Button";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { remittanceActions } from "../../../state/remittanceSlice";
import { remittanceApi } from "../../../api/remittanceApi";
import { toast } from "react-toastify";
import { AppStrings, logApiErrorToSentry } from "../../../utils";
import SelectDropdown from "../../ui/SelectDropdown";
import { DebounceInput } from "react-debounce-input";
import Countdown, { zeroPad } from "react-countdown";

interface AddRemitterModalProps {
  visible: boolean;
  onSubmit: (type: any) => void;
  onClose: () => void;
}

export const AddRemitterModal: React.FC<AddRemitterModalProps> = ({ 
  visible,
  onSubmit, 
  onClose
 }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { transactionDetail, payment, remitter } = useAppSelector((state) => state.remittance);

  const [emailError, setEmailError] = React.useState("");
  const [mobileError, setMobileError] = React.useState("");
  const [mobileOTPError, setOTPError] = React.useState("");
  const [skipOtp, setSkipOtp] = React.useState(false);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidMobile = (mobile: string) => /^[6-9]\d{9}$/.test(mobile);

  const disabled = !remitter?.addRemitterInput?.email || !remitter?.addRemitterInput?.mobile || emailError || mobileError || (remitter?.addRemitterInput?.isSentOtp && !skipOtp && remitter?.addRemitterInput?.otp?.length != 6);
  const sendKycDisabled = !remitter?.addRemitterInput?.email || !remitter?.addRemitterInput?.mobile || emailError || mobileError;
  const loading = remitter?.addRemitter?.sendOTPLoading || remitter?.addRemitter?.validateOTPLoading || remitter?.addRemitter?.signUpLoading;

  const onCloseIcon = () => {
    setEmailError("");
    setMobileError("");
    setOTPError("");
    setSkipOtp(false);
    dispatch(remittanceActions.setAddRemitterInput({ email: "", mobile: "", otp: "", isSentOtp: false }));
    onClose();
  }

  React.useEffect(() => {
    if (!visible) {
      setSkipOtp(false);
    }
  }, [visible]);

  return (
    <Modal
      header="Remitter contact details"
      headerClass="flex items-center justify-between w-full text-[22px] font-semibold"
      modalClass="bg-gray-200"
      visible={visible}
      maskClosable={false}
      closeHandler={onCloseIcon}
      closable={true}
    >
      {/* BODY */}
      <div className="flex flex-col mt-5">

        <span className="w-[460px] text-black-100 text-left text-[15px] mt-5">Email
          <span className="text-red-100 text-sm pl-1">
            {"*"}
          </span>
        </span>
        <form autoComplete="off">
        <DebounceInput
          className={"w-[460px] rounded-md p-[3.4%] bg-[#fff] border text-base outline-none disabled:bg-gray-100"}
          name="user_email_field" 
          type="text"
          autoComplete="off"
          value={remitter?.addRemitterInput?.email || ""}
          debounceTimeout={0}
          onChange={(e: any) => {
            const value = e.target.value;
            dispatch(remittanceActions.setAddRemitterInput({ email: value }));

            if (value && !isValidEmail(value)) {
              setEmailError("Please enter a valid email");
            } else {
              setEmailError("");
            }
          }}
          placeholder="Enter your email"
          disabled={remitter?.addRemitterInput?.isSentOtp}
        />
        </form>

        {emailError && (
          <span className="text-red-500 text-sm mt-1">{emailError}</span>
        )}

        <span className="float-left text-black-100 text-left text-[15px] mt-5">Mobile Number
          <span className="text-red-100 text-sm pl-1">
            {"*"}
          </span>
        </span>
        <DebounceInput
          type="text"
          maxLength={10}
          className="w-full rounded-md p-[3.4%] bg-[#fff] border text-base outline-none disabled:bg-gray-100"
          value={remitter?.addRemitterInput?.mobile || ""}
          debounceTimeout={0}
          onChange={(e: any) => {
            const raw = e.target.value;
            const cleaned = raw.slice(0, 10);
            dispatch(
              remittanceActions.setAddRemitterInput({ mobile: cleaned })
            );
            if (cleaned.length !== 10 || !isValidMobile(cleaned)) {
              setMobileError(
                cleaned ? "Please enter a valid 10 digit mobile number" : ""
              );
            } else {
              setMobileError("");
            }
          }}
          placeholder="Enter your mobile number"
          disabled={remitter?.addRemitterInput?.isSentOtp}
        />

        {mobileError && (
          <span className="text-red-500 text-sm mt-1">{mobileError}</span>
        )}

        {remitter?.addRemitterInput?.isSentOtp && !skipOtp && (
          <>
            <span className="float-left text-black-100 text-left text-[15px] mt-5">OTP
              <span className="text-red-100 text-sm pl-1">
                {"*"}
              </span>
            </span>
            <DebounceInput
              type="text"
              maxLength={6}
              className="w-full rounded-md p-[3.4%] bg-[#fff] border text-base outline-none"
              value={remitter?.addRemitterInput?.otp || ""}
              debounceTimeout={0}
              onChange={(e: any) => {
                const raw = e.target.value;
                const cleaned = raw.slice(0, 6);
                dispatch(
                  remittanceActions.setAddRemitterInput({ otp: cleaned })
                );
                if (cleaned.length !== 6) {
                  setOTPError(
                    cleaned ? "Please enter a valid OTP" : ""
                  );
                } else {
                  setOTPError("");
                }
              }}
              placeholder="Enter your mobile number"
            />
          </>
        )}

        <div className="flex items-center mt-8 gap-2">
          <Button
            bgColor={disabled ? "bg-gray-300" : "bg-[#006EFF]"}
            fontStyle={{
              fontFamily: "font-Inter",
              fontSize: "text-sm",
              fontWeight: "font-bold",
              fontColor: "text-white",
            }}
            borderStyle={{ borderRadius: "rounded-lg" }}
            className={`p-4 ${!disabled && "hover:bg-blue-600"} w-[10rem]`}
            onClickHandler={() =>
              onSubmit(remitter?.addRemitterInput?.isSentOtp ? "proceed" : skipOtp ? "withoutotp" : "verify")
            }
            disabled={disabled || loading}
            isLoading={loading}
          >
            <div className='flex items-center gap-1'>
              {remitter?.addRemitterInput?.isSentOtp || skipOtp ? "Proceed" : "Verify"}
            </div>
          </Button>
          {!remitter?.addRemitterInput?.isSentOtp && !skipOtp && (
            <Button
              bgColor={"bg-[#F3F3F4]"}
              fontStyle={{
                fontFamily: "font-ssp",
                fontSize: "text-sm",
                fontWeight: "font-semibold",
                fontColor: "text-black",
              }}
              borderStyle={{ borderRadius: "rounded-lg" }}
              className={`p-4 w-[10rem]`}
              onClickHandler={() => setSkipOtp(true)}
              disabled={sendKycDisabled}
            >
              <div className='flex items-center gap-1'>
                {"SKIP OTP"}
              </div>
            </Button>
          )}
          {(remitter?.addRemitterInput?.isSentOtp || skipOtp) && (
            <Button
              bgColor={"bg-[#F3F3F4]"}
              fontStyle={{
                fontFamily: "font-ssp",
                fontSize: "text-sm",
                fontWeight: "font-semibold",
                fontColor: "text-black",
              }}
              borderStyle={{ borderRadius: "rounded-lg" }}
              className={`p-4 w-[10rem]`}
              onClickHandler={() => onSubmit("sendkyc")}
              disabled={sendKycDisabled || remitter?.addRemitter?.sendKycLinkLoading}
              isLoading={remitter?.addRemitter?.sendKycLinkLoading}
            >
              <div className='flex items-center gap-1'>
                {"SEND KYC LINK"}
              </div>
            </Button>
          )}
        </div>

        {remitter?.addRemitterInput?.isSentOtp && !skipOtp && (
        <div className="flex items-center justify-center align-middle mt-8">
          <Countdown
            key={remitter?.addRemitterInput?.resendCount}
            date={remitter?.addRemitterInput?.date}
            renderer={({ minutes, seconds, completed }) => {
              if (completed) {
                if(remitter?.addRemitter?.resendOTPLoading) {
                  return(
                    <div className="p-4 flex justify-center items-center">
                      <div className="animate-spin h-6 w-6 border-2 border-t-blue-500 rounded-full" />
                    </div>
                  )
                } else {
                  return (
                    <div
                      className="text-xs font-bold text-black cursor-pointer font-ssp"
                      onClick={() => onSubmit("resendotp")}
                    >
                      Resend
                    </div>
                  );
                }
              } else {
                return (
                  <div className="flex items-center space-x-2">
                    <div className="text-xs font-bold text-black font-ssp">
                      Resend
                    </div>
                    <div
                      className={
                        "text-xs font-bold text-primary font-ssp"
                      }
                    >
                      {zeroPad(minutes)} :{zeroPad(seconds)}
                    </div>
                  </div>
                );
              }
            }}
          />
        </div>
        )}

      </div>
    </Modal>
  );
};
